import { Trans, useTranslation } from 'react-i18next';
import { DropdownStyled } from 'src/components';
import { localUrl } from 'src/utils';

import { VENDOR_DOWNLOAD_LINKS, detectBrowserKey, vendorFaviconUrl } from '../constants';

export interface VendorOption {
  vendorId: string;
  vendorDisplayName: string;
  showVendor: boolean;
}

interface Props {
  vendors: VendorOption[];
  value: string;
  onChange: (vendorId: string) => void;
  disabled?: boolean;
}

// Custom retailer dropdown: searchable vendors + a CTA to install the extension
// for the long tail of unsupported-for-search retailers.
export const VendorSelect = ({ vendors, value, onChange, disabled }: Props) => {
  const { t } = useTranslation();

  const selected = vendors.find((v) => v.vendorId === value);
  const downloadUrl =
    VENDOR_DOWNLOAD_LINKS[detectBrowserKey()] || VENDOR_DOWNLOAD_LINKS.chrome;

  // Searchable vendors, Walmart first, then alphabetical (already sorted alpha upstream).
  const options = vendors
    .filter((v) => v.showVendor || v.vendorId === value)
    .sort((a, b) => {
      if (a.vendorDisplayName === 'Walmart') return -1;
      if (b.vendorDisplayName === 'Walmart') return 1;
      return a.vendorDisplayName.localeCompare(b.vendorDisplayName);
    });

  return (
    <div className='relative w-[200px] xs:w-[240px] sm:w-[280px] shrink-0'>
      <DropdownStyled
        darkStyle={false}
        disabled={disabled}
        className={`w-full ${disabled ? 'opacity-50' : ''}`}
        buttonContent={
          <div className='flex items-center gap-2'>
            <div className='bg-white flex items-center justify-center rounded-[4px] w-8 h-8 shrink-0'>
              {selected && (
                <img
                  className='w-6 h-6'
                  src={vendorFaviconUrl(selected.vendorId)}
                  alt={`${selected.vendorDisplayName} logo`}
                />
              )}
            </div>
            <span className='font-secondary font-semibold text-base text-primary-dark'>
              {selected ? selected.vendorDisplayName : t('common.select_retailer')}
            </span>
          </div>
        }
      >
        {(closeDropdown) => (
          <div className='w-[200px] xs:w-[240px] sm:w-[280px] bg-cloudGray rounded-lg'>
            <div
              role='listbox'
              aria-label='Select retailer'
              className='max-h-[215px] overflow-y-auto mb-4'
            >
              {options.map((v) => (
                <div
                  key={v.vendorId}
                  role='option'
                  aria-selected={v.vendorId === value}
                  onClick={() => {
                    onChange(v.vendorId);
                    closeDropdown();
                  }}
                  className='flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-primary/10 rounded-md'
                >
                  <img
                    src={vendorFaviconUrl(v.vendorId)}
                    className='w-6 h-6 rounded-sm shrink-0'
                    alt={`${v.vendorDisplayName} logo`}
                  />
                  <span className='font-secondary font-semibold text-base text-primary'>
                    {v.vendorDisplayName}
                  </span>
                </div>
              ))}
            </div>

            <div className='bg-skyBlue rounded-lg p-3 flex gap-3 items-start'>
              <span className='material-icons-outlined text-[#171885] shrink-0 text-[24px] leading-none'>
                info
              </span>
              <div className='flex flex-col gap-2 min-w-0'>
                <div className='flex flex-col gap-1'>
                  <p className='font-primary font-semibold text-sm text-[#171885] leading-tight'>
                    {t('make.vendor_cta_heading')}
                  </p>
                  <p className='font-secondary font-normal text-xs text-[#171885] leading-relaxed'>
                    <Trans
                      i18nKey='make.vendor_cta_body'
                      components={[
                        // Children are replaced by the translated text at runtime.
                        <a
                          href={localUrl('/supported')}
                          className='underline font-semibold hover:opacity-60 transition'
                        >
                          full list
                        </a>,
                      ]}
                    />
                  </p>
                </div>
                <a
                  href={downloadUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  data-attr='download-cta'
                  className='group flex items-center gap-2 w-fit'
                >
                  <span className='font-secondary font-semibold text-sm text-[#171885] group-hover:opacity-60 transition'>
                    {t('make.vendor_cta_link')}
                  </span>
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    aria-hidden='true'
                    className='shrink-0 group-hover:opacity-60 transition'
                  >
                    <path
                      d='M15.8333 15.8333H4.16667V4.16667H10V2.5H4.16667C3.24167 2.5 2.5 3.25 2.5 4.16667V15.8333C2.5 16.75 3.24167 17.5 4.16667 17.5H15.8333C16.75 17.5 17.5 16.75 17.5 15.8333V10H15.8333V15.8333ZM11.6667 2.5V4.16667H14.6583L6.46667 12.3583L7.64167 13.5333L15.8333 5.34167V8.33333H17.5V2.5H11.6667Z'
                      fill='#171885'
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}
      </DropdownStyled>
    </div>
  );
};
